import { PreviewSectionContext } from '../../../utils/previewMapSections';

export type PreviewRow = { uuid: string; name: string };

type NamedEntity = { uuid: string };
type ClassEntity = NamedEntity & { shortName?: string | null; name: string };
type TeacherEntity = NamedEntity & { displayNameShort?: string | null; shortName: string };
type RoomEntity = NamedEntity & { name: string };

const byName = (a: PreviewRow, b: PreviewRow) => a.name.localeCompare(b.name, undefined, { numeric: true });

/** Alle Zeilen eines Vorschau-Kontexts, alphabetisch – unabhängig vom Board-Kontext. */
export function buildRows(
  context: PreviewSectionContext,
  classes: Iterable<ClassEntity>,
  teachers: Iterable<TeacherEntity>,
  rooms: Iterable<RoomEntity>,
): PreviewRow[] {
  if (context === 'teachers') {
    return Array.from(teachers, (teacher) => ({
      uuid: teacher.uuid,
      name: teacher.displayNameShort ?? teacher.shortName,
    })).sort(byName);
  }
  if (context === 'rooms') {
    return Array.from(rooms, (room) => ({ uuid: room.uuid, name: room.name })).sort(byName);
  }
  return Array.from(classes, (schoolClass) => ({
    uuid: schoolClass.uuid,
    name: schoolClass.shortName ?? schoolClass.name,
  })).sort(byName);
}

/**
 * Raumvorrat einer Karte: derselbe Pool wie in „Karte bearbeiten" (Lesson-/Fach-/Lehrer-/
 * Klassenräume, siehe `defaultCardRooms.allUnique`) plus die aktuell zugewiesenen Räume.
 */
export function cardRoomSupply(
  cardUuid: string,
  cardRoomUuids: Iterable<string>,
  defaultCardRooms: Map<string, { allUnique: { value: string }[] }>,
): Set<string> {
  const allowed = new Set<string>();
  defaultCardRooms.get(cardUuid)?.allUnique.forEach((room) => allowed.add(room.value));
  for (const uuid of cardRoomUuids) allowed.add(uuid);
  return allowed;
}

/**
 * Alle Klassen, in denen eine der Lehrkräfte irgendein Kärtchen hat. Im Lehrer-Board ist das die
 * sinnvolle Klassen-Auswahl: nicht nur die Klassen DIESER Karte, sondern der gesamte Wirkungskreis
 * der Lehrkraft – dort will man beim Stecken freie Slots erkennen.
 */
export function teacherClassUuids(
  teacherUuids: Set<string>,
  cardTeacher: Map<string, Set<string>>,
  cardClasses: Map<string, Set<string>>,
): Set<string> {
  const classUuids = new Set<string>();
  cardTeacher.forEach((teachers, cardUuid) => {
    let hit = false;
    teachers.forEach((teacherUuid) => {
      if (teacherUuids.has(teacherUuid)) hit = true;
    });
    if (!hit) return;
    cardClasses.get(cardUuid)?.forEach((classUuid) => classUuids.add(classUuid));
  });
  return classUuids;
}

/**
 * Nachbarstunden der Karte in der Hand: der Slot vor der ersten und nach der letzten Stunde am
 * selben Tag. Pausen zählen nicht mit (`previewHours` enthält sie nicht), am Tagesrand entfällt der
 * jeweilige Nachbar.
 */
export function adjacentHourUuids(
  hourUuids: string[],
  previewHours: { value: string }[],
): { previous: string | null; next: string | null } {
  if (hourUuids.length === 0) return { previous: null, next: null };
  const first = previewHours.findIndex((hour) => hour.value === hourUuids[0]);
  const last = previewHours.findIndex((hour) => hour.value === hourUuids[hourUuids.length - 1]);
  return {
    previous: first > 0 ? previewHours[first - 1].value : null,
    next: last >= 0 && last < previewHours.length - 1 ? previewHours[last + 1].value : null,
  };
}

export type AdjacentRooms = {
  /** `${hourUuid}:${day}:${roomUuid}` → Position, für die Markierung der Zelle im Raster. */
  cells: Map<string, 'previous' | 'next'>;
  /** Räume, in denen die Klassen unmittelbar davor bzw. danach sind – für die Textzeilen. */
  previousRoomUuids: string[];
  nextRoomUuids: string[];
};

/**
 * Wo sind die Klassen der Karte unmittelbar vor bzw. nach dem Zielslot? Damit sieht man beim
 * Raumtausch die Laufwege. Teilungen/Klassengruppen fallen automatisch mit ab: Gruppenkarten
 * stehen im Slot-Index unter der Klassen-UUID.
 */
export function adjacentRoomCells({
  classUuids,
  day,
  hourUuids,
  previewHours,
  cardsBySlotAndResource,
  cardRooms,
  pickedCardUuid,
}: {
  classUuids: Iterable<string>;
  day: string;
  hourUuids: string[];
  previewHours: { value: string }[];
  cardsBySlotAndResource: Map<string, Set<string>>;
  cardRooms: Map<string, Map<string, { value: string }>>;
  pickedCardUuid: string;
}): AdjacentRooms {
  const cells = new Map<string, 'previous' | 'next'>();
  const roomUuids = { previous: new Set<string>(), next: new Set<string>() };
  const { previous, next } = adjacentHourUuids(hourUuids, previewHours);

  (
    [
      ['previous', previous],
      ['next', next],
    ] as const
  ).forEach(([position, hourUuid]) => {
    if (!hourUuid) return;
    for (const classUuid of classUuids) {
      cardsBySlotAndResource.get(`${hourUuid}:${day}:${classUuid}`)?.forEach((cardUuid) => {
        if (cardUuid === pickedCardUuid) return;
        cardRooms.get(cardUuid)?.forEach((room) => {
          cells.set(`${hourUuid}:${day}:${room.value}`, position);
          roomUuids[position].add(room.value);
        });
      });
    }
  });

  return {
    cells,
    previousRoomUuids: [...roomUuids.previous],
    nextRoomUuids: [...roomUuids.next],
  };
}
